// ─── Influencer Explorer constants ───────────────────────────────────────────
// A cross-platform creator directory (TOP_INFLUENCER_FILTERS.md) - its own
// top-level section, not a tab bolted onto UGC Explorer's per-app video
// coverage. Kept as its own constants file (rather than importing UGC
// Explorer's) so this section can evolve its platform registry/copy
// independently of the video tabs.

// Sitewide pink accent - kept in sync with every other section's primary
// accent (UGC Explorer, Ad Discovery, etc.) rather than a one-off color, so
// this section's buttons/active states/badges read identically to the rest
// of the app.
export const INFLUENCER_ACCENT_COLOR = "#F6255A"

// 21, not 20 - the grid renders 3 cards per row (7 rows of 21), so a page
// size of 20 always leaves the last row's third slot empty even though
// there's a 21st result waiting on the next page.
export const INFLUENCER_PAGE_SIZE = 21

// Platform registry - id/label/icon/color per platform this directory
// covers. Mirrors UGC Explorer's own UGC_PLATFORMS list so the two sections
// present platforms identically, without either importing the other.
export const INFLUENCER_PLATFORMS = [
  { id: "youtube", label: "YouTube", icon: "fab fa-youtube", color: "#FF0000" },
  { id: "instagram", label: "Instagram", icon: "fab fa-instagram", color: "#E1306C" },
  { id: "tiktok", label: "TikTok", icon: "fab fa-tiktok", color: "#25F4EE" },
]

// §3 - every `sort` value the endpoint accepts. Aliases (`top`/`size`/…)
// resolve server-side; the button only ever shows the canonical value.
export const INFLUENCER_SORT_OPTIONS = [
  { value: "audience", label: "Audience Size" },
  { value: "followers", label: "Followers" },
  { value: "subscribers", label: "Subscribers" },
  { value: "views", label: "Total Views" },
  { value: "posts", label: "Most Posts" },
  { value: "coverage", label: "App Coverage" },
  { value: "newest", label: "Newest Discovered" },
  { value: "recently_updated", label: "Recently Updated" },
  { value: "smallest", label: "Smallest First" },
  { value: "name", label: "Name (A-Z)" },
]

// §2 "Platform" - CSV multi-select. No separate options list here: the
// toolbar's InfluencerPlatformToggle reads platform id/label/icon/color
// straight off INFLUENCER_PLATFORMS above.

// Tier labels for the unified `creator_size` band (TOP_INFLUENCER_FILTERS.md
// §2) - shared by InfluencerCard and the profile page's header, both of
// which render the same band on the same row shape.
export const INFLUENCER_TIER_LABEL = {
  nano: "Nano",
  micro: "Micro",
  mid: "Mid",
  macro: "Macro",
  mega: "Mega",
}

// §2 "Audience size" - the unified, platform-blind bands (`audience_size`),
// not any single platform's own follower/subscriber field.
export const INFLUENCER_CREATOR_SIZE_OPTIONS = [
  { value: "nano", label: "Nano" },
  { value: "micro", label: "Micro" },
  { value: "mid", label: "Mid" },
  { value: "macro", label: "Macro" },
  { value: "mega", label: "Mega" },
]

// Shared by `discovered_within_days` and `refreshed_within_days` (§2
// "Freshness").
export const INFLUENCER_WITHIN_DAYS_OPTIONS = [
  { value: "", label: "Any time" },
  { value: "7", label: "Last 7 days" },
  { value: "14", label: "Last 14 days" },
  { value: "30", label: "Last 30 days" },
  { value: "90", label: "Last 90 days" },
]

// Chip copy/colors for a post's `ai_summary.content_type`
// (INFLUENCER_PROFILE_API.md §3). Same idea as UGC Explorer's own
// CONTENT_TYPE_META, kept as a separate, local copy (this feature's own
// utils.js/constants.js are already trimmed local copies rather than
// cross-imports - see utils.js's header comment) - and a slightly larger
// enum than UGC Explorer's own list (adds `trailer` and `meme`).
export const INFLUENCER_POST_CONTENT_TYPE_META = {
  review: { label: "Review", color: "#3b82f6" },
  gameplay: { label: "Gameplay", color: "#8b5cf6" },
  tutorial: { label: "Tutorial", color: "#10b981" },
  news: { label: "News", color: "#f59e0b" },
  announcement: { label: "Announcement", color: "#f59e0b" },
  trailer: { label: "Trailer", color: "#3b82f6" },
  comparison: { label: "Comparison", color: "#3b82f6" },
  tier_list: { label: "Tier List", color: "#8b5cf6" },
  meme: { label: "Meme", color: "#F6255A" },
  monetisation: { label: "Monetisation", color: "#ef4444" },
  criticism: { label: "Criticism", color: "#ef4444" },
  promotion: { label: "Promotion", color: "#F6255A" },
  other: { label: "Other", color: "#6b7280" },
  unknown: { label: "Unclassified", color: "#6b7280" },
}

// Chip copy/colors for a post's `ai_summary.sentiment` - same values UGC
// Explorer uses, plus INFLUENCER_PROFILE_API.md's `mixed`.
export const INFLUENCER_POST_SENTIMENT_META = {
  positive: { label: "Positive", color: "#10b981" },
  neutral: { label: "Neutral", color: "#6b7280" },
  negative: { label: "Negative", color: "#ef4444" },
  mixed: { label: "Mixed", color: "#f59e0b" },
  unknown: { label: "Unknown", color: "#6b7280" },
}

// The Influencer Explorer toolbar's "All Influencers" / "My Favourites"
// segmented toggle (InfluencerModeToggle) - INFLUENCER_FAVOURITES_API.md §4's
// saved list takes every filter/sort the ranking does, so switching mode
// only changes which endpoint InfluencerSection's data hook calls.
export const INFLUENCER_MODE = {
  ALL: "all",
  FAVOURITES: "favourites",
}

// Empty/default filter shape (§2, in full) - camelCase to match this
// codebase's convention; topInfluencerApi.js's FILTER_PARAM_MAP translates to
// the backend's snake_case params.
export const DEFAULT_FILTERS_INFLUENCER = {
  platform: [],
  creatorSize: [],
  minFollowers: "",
  maxFollowers: "",
  minPosts: "",
  maxPosts: "",
  minTotalViews: "",
  maxTotalViews: "",
  minPostsAboutApps: "",
  appId: "",
  verifiedOnly: false,
  hasEmail: false,
  hasLinks: false,
  hasDescription: false,
  excludePrivate: false,
  mirroredImageOnly: false,
  search: "",
  username: "",
  country: "",
  discoveredWithinDays: "",
  refreshedWithinDays: "",
  staleOnly: false,
}
